import React, { useEffect, useState, useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import PermittedComponent from "PermittedComponent"
import Loader from "components/Loader/Loader"
import { Card, CardBody, Input, Table, Button, Badge } from "reactstrap"
import { usePermissionData } from "AppContext"
import { fetchData, FetchPutData } from "utils/Common_api_functions"
import { FaCheck, FaSearch } from "react-icons/fa"
import { MdCancel } from "react-icons/md"
import moment from "moment"
import Swal from "sweetalert2"

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
]

const currentYear = moment().year()
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i)

const PaidOff = () => {
  document.title = "Paid Off | Vintage Infra Equipment Sol PVT LTD"
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState("")
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1)
  const [selectedYear, setSelectedYear] = useState(moment().year())

  useEffect(() => {
    fetchPaidOff()
  }, [])

  async function fetchPaidOff() {
    const APIdata = await fetchData(setLoading, "paid/off")
    if (APIdata?.success) {
      setData(APIdata?.data?.reverse())
    }
  }

  // ── Approve / Reject ──────────────────────────────────────
  const handleStatusChange = async (id, status) => {
    const confirm = await Swal.fire({
      title: `${status === 1 ? "Approve" : "Reject"} this request?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: status === 1 ? "#28a745" : "#dc3545",
    })
    if (!confirm.isConfirmed) return

    const res = await FetchPutData(setLoading, "paid/off", id, { status })
    if (res?.data?.success) {
      Swal.fire(
        "Done!",
        `Request has been ${status === 1 ? "Approved" : "Rejected"}.`,
        "success"
      )
      fetchPaidOff()
    } else {
      Swal.fire(
        "Error",
        res?.response?.data?.message || "Something went wrong",
        "error"
      )
    }
  }

  // ── Search + Month/Year filter ────────────────────────────
  const filteredData = useMemo(() => {
    let result = data

    // Month filter
    if (selectedMonth) {
      result = result.filter(item => {
        if (!item.inDate) return false
        return moment(item.inDate).month() + 1 === Number(selectedMonth)
      })
    }

    // Year filter
    if (selectedYear) {
      result = result.filter(item => {
        if (!item.inDate) return false
        return moment(item.inDate).year() === Number(selectedYear)
      })
    }

    // Search filter
    if (search.trim()) {
      const lower = search.toLowerCase()
      result = result.filter(
        item =>
          String(item.userId).includes(lower) ||
          (item.inDate && item.inDate.includes(lower)) ||
          (item.inDay && item.inDay.toLowerCase().includes(lower)) ||
          (item.status && item.status.toLowerCase().includes(lower))
      )
    }

    return result
  }, [data, search, selectedMonth, selectedYear])

  // ── Pagination ────────────────────────────────────────────
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize)

  // Reset to page 1 on filter/search change
  useEffect(
    () => setCurrentPage(1),
    [search, pageSize, selectedMonth, selectedYear]
  )

  const { checkAccess } = usePermissionData()

  // ── Clear all filters ─────────────────────────────────────
  const handleClearFilters = () => {
    setSelectedMonth("")
    setSelectedYear("")
    setSearch("")
  }

  const hasActiveFilters = selectedMonth || selectedYear || search

  // ── Status badge ──────────────────────────────────────────
  const renderBadge = status => {
    if (!status)
      return (
        <Badge color="warning" className="p-2">
          Pending
        </Badge>
      )
    if (status === true)
      return (
        <Badge color="success" className="p-2">
          Approved
        </Badge>
      )
    if (status === false)
      return (
        <Badge color="danger" className="p-2">
          Rejected
        </Badge>
      )
    return <Badge color="secondary">{status}</Badge>
  }

  return (
    <PermittedComponent isAccess={checkAccess(8, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Leave Management" breadcrumbItem="Paid Off" />
        <Card>
          <CardBody>
            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex gap-3 align-items-center flex-wrap">
                {/* Page size */}
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Input>

                {/* Month filter */}
                <Input
                  type="select"
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  className="rounded-4 w-auto"
                >
                  <option value="">All Months</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>

                {/* Year filter */}
                <Input
                  type="select"
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                  className="rounded-4 w-auto"
                >
                  <option value="">All Years</option>
                  {YEARS.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>

                {/* Search */}
                <div className="position-relative" style={{ minWidth: 220 }}>
                  <FaSearch
                    className="text-muted"
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="rounded-4 ps-4"
                  />
                  {search && (
                    <MdCancel
                      onClick={() => setSearch("")}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Table ── */}
            <Table striped responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>User Name</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>In Time</th>
                  <th>Out Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr key={row.id}>
                      <td>{startIndex + index + 1}</td>
                      <td>{row.user?.name}</td>
                      <td>
                        {row.inDate
                          ? moment(row.inDate).format("DD-MM-YYYY")
                          : "--"}
                      </td>
                      <td>{row.inDay || "--"}</td>
                      <td>{row.inTime || "--"}</td>
                      <td>{row.outTime || "--"}</td>
                      <td className="d-flex gap-2">
                        {renderBadge(row.status)}
                        {checkAccess(8, "edit") &&
                          (row.status === true || row.status === false ? (
                            <></>
                          ) : (
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={() => handleStatusChange(row.id, 1)}
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => handleStatusChange(row.id, 0)}
                              >
                                <MdCancel />
                              </Button>
                            </div>
                          ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <nav className="d-flex justify-content-between align-items-center mt-3">
                <h6 className="mb-0 text-muted">
                  Showing {filteredData.length === 0 ? 0 : startIndex + 1} to{" "}
                  {Math.min(startIndex + pageSize, filteredData.length)} of{" "}
                  {filteredData.length} entries
                </h6>
                <ul className="pagination mb-0">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li
                      key={i + 1}
                      className={`page-item ${
                        currentPage === i + 1 ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </CardBody>
        </Card>
      </div>
    </PermittedComponent>
  )
}

export default PaidOff
